import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import validid from 'validid';
import Select from 'react-select';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faCaretUp } from '@fortawesome/free-solid-svg-icons';
import { Common, PopUpBox } from '../../../components';
import {
  styles,
  options,
  numberWithNoCommas,
  detectMob,
  pad,
  capitalizeName,
  alphabetOnly,
  alphabetAndNumberOnly,
  normalizeRefRecommenderNo,
  isValidRefRecommenderNo,
  companyNameOnly,
  phoneNumberOnly,
  isOptionalHongKongPhone,
  isOptionalMaxLength,
  numberWithCommas,
  OPTIONAL_EMAIL_PATTERN,
  RESI_OWNER_OPTIONS,
} from '../../../utils/common';
import { actionGetStateDoc } from '../../../store/application/actions';
import { actionAddressConfig, actionApplicationIP, actionTracking } from '../../../store/common/actions';
import DropdownIndicator from '../../../components/Common/DropdownIndicator';
import { actionLendelaAppInfo } from '../../../store/lendela/action';
import { buildConsentRouteState, createApplicationFlowMeta } from '../../../utils/applicationDraft';
import { APPLICATION_FLOW_STEP, buildApplicationFlowPreviousRoute, canMoveApplicationFlowHistoryBack, getApplicationFlowHistoryIndex, isApplicationFlowBackPopState } from '../../../utils/applicationFlowBackNavigation';
import { QUICK_CASH_APPLY_TERM_OPTIONS, QUICK_CASH_FIXED_LOAN_AMOUNT, QUICK_CASH_PRODUCT_CODE } from '../config';
import { QUICK_CASH_ROUTES } from '../routes';
import { getQuickCashDraft, saveQuickCashDraft } from '../storage';

const PAY_DAY_OPTIONS = Array.from({ length: 31 }, (_, index) => {
  const value = String(index + 1);
  return { value, label: value };
});

const buildAddressLine1 = (room, floor, block) => {
  let addrLine1 = '';

  if (room) {
    addrLine1 = `RM ${(room ?? '')
      .replace(new RegExp('ROOM', 'g'), '')
      .replace(new RegExp('RM', 'g'), '')
      .replace(new RegExp('FLAT', 'g'), '')
      .replace(new RegExp('FLT', 'g'), '')
      .replace(new RegExp('UNIT', 'g'), '')
      .trim()}`;
  }

  if (floor) {
    addrLine1 += ` ${(floor ?? '')
      .replace(new RegExp('FLOOR', 'g'), '')
      .replace(new RegExp('F', 'g'), '')
      .replace(new RegExp('/F', 'g'), '')
      .trim()}/F`;
  }

  if (block) {
    addrLine1 += ` BLK ${(block ?? '')
      .replace(new RegExp('BLK', 'g'), '')
      .replace(new RegExp('BLOCK', 'g'), '')
      .replace(new RegExp('BL', 'g'), '')
      .trim()}`;
  }

  return addrLine1.trim();
};

const normalizeDateValue = (dateValue) => {
  return dateValue ? `${dateValue}`.replace(/[-/]/g, '') : '';
};

const formatSelectedDate = (dateValue) => {
  const normalizedDate = normalizeDateValue(dateValue);
  if (normalizedDate.length !== 8) {
    return null;
  }

  return `${normalizedDate.substring(0, 4)}/${normalizedDate.substring(4, 6)}/${normalizedDate.substring(6, 8)}`;
};

const formatDateParts = (year, month, day) => {
  return year && month && day ? `${year}${pad(month)}${pad(day)}` : '';
};

const getFallbackAddressConfig = () => ({
  area: options.area,
  district_hk: options.district_hk,
  district_kw: options.district_kw,
  district_nt: options.district_nt,
});

function QuickCashForm() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  // 일반 신청 Form과 런타임 설정을 공유하지 않고 Quick Cash 정책을 직접 사용한다.
  const readDraft = getQuickCashDraft;
  const writeDraft = saveQuickCashDraft;
  const flowRoutes = QUICK_CASH_ROUTES;
  const fixedLoanAmount = QUICK_CASH_FIXED_LOAN_AMOUNT;
  const hasFixedLoanAmount = true;
  const applyTermOptions = QUICK_CASH_APPLY_TERM_OPTIONS;
  const applicationDraft = readDraft();
  const restoredState = !location.state?.phone ? buildConsentRouteState(applicationDraft?.meta) : null;
  const pageState = location.state ?? restoredState;
  const fallbackPhone = pageState?.phone || localStorage.getItem('application_phone') || '';
  const flowMeta = createApplicationFlowMeta({
    phone: pageState?.phone,
    lendelaAppId: pageState?.lendela_app_id,
    noDocLoanData: pageState?.no_doc_loan_data,
  });
  const matchedDraft = flowMeta?.phone ? readDraft(flowMeta) : applicationDraft;
  const getLatestMatchedDraft = () => {
    if (!flowMeta?.phone) {
      return applicationDraft;
    }

    return readDraft(flowMeta) ?? applicationDraft;
  };
  const {
    setValue,
    register,
    handleSubmit,
    getValues,
    setError,
    clearErrors,
    setFocus,
    formState: { errors, isSubmitting },
  } = useForm();

  const [gender, setGender] = useState(null);
  const [genderDefault, setGenderDefault] = useState(null);
  const [isVisibleInvalidToken] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [hkid2, setHkid2] = useState('');
  const [checkBoxTime, setCheckBoxTime] = useState({});
  const [lendelaData, setLendelaData] = useState(null);
  const [loanPurposeDefault, setLoanPurposeDefault] = useState(null);
  const [dateOfBirthDefault, setDateOfBirthDefault] = useState(null);
  const [joinDateDefault, setJoinDateDefault] = useState(null);
  const [moveInDateDefault, setMoveInDateDefault] = useState(null);
  const [emailDefault, setEmailDefault] = useState('');
  const [amountDefault, setAmountDefault] = useState(null);
  const [applyTermDefault, setApplyTermDefault] = useState(null);
  const [educationDefault, setEducationDefault] = useState(null);
  const [marriageStatusDefault, setMarriageStatusDefault] = useState(null);
  const [addressConfig, setAddressConfig] = useState(getFallbackAddressConfig());
  const [areaDefault, setAreaDefault] = useState(null);
  const [districtOptions, setDistrictOptions] = useState([]);
  const [districtValue, setDistrictValue] = useState(null);
  const [estate, setEstate] = useState('');
  const [optionalExpanded, setOptionalExpanded] = useState(false);
  const [workAreaDefault, setWorkAreaDefault] = useState(null);
  const [workDistrictOptions, setWorkDistrictOptions] = useState([]);
  const [workDistrictValue, setWorkDistrictValue] = useState(null);
  const [payDayDefault, setPayDayDefault] = useState(null);
  const [resiOwnerDefault, setResiOwnerDefault] = useState(null);
  const [numberLivingWithDefault, setNumberLivingWithDefault] = useState(null);
  const [monthlyRent, setMonthlyRent] = useState('');

  useEffect(() => {
    if (pageState?.lendela_app_id) {
      async function getLendelaInfo() {
        const response = await dispatch(actionLendelaAppInfo(pageState.lendela_app_id));
        if (response?.sts_code === 0) {
          const data = response.content;
          data.offer.loanAmount = parseInt(data?.offer?.loanAmount, 10);
          setLendelaData(data);
          fillLendelaData(data);
        }
      }
      getLendelaInfo();
    }
  }, [dispatch, pageState?.lendela_app_id]);

  useEffect(() => {
    if (pageState?.no_doc_loan_data) {
      fillNoDocLoanData(pageState.no_doc_loan_data);
    }
  }, [pageState?.no_doc_loan_data]);

  useEffect(() => {
    async function loadAddressConfig() {
      const response = await dispatch(actionAddressConfig());
      if (response?.sts_code === 0 && response?.content) {
        setAddressConfig(response.content);
        return;
      }

      setAddressConfig(getFallbackAddressConfig());
    }

    loadAddressConfig();
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      actionTracking({
        url: window.location.href,
        product_code: QUICK_CASH_PRODUCT_CODE,
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!location.state?.phone) {
      if (restoredState?.phone) {
        navigate(flowRoutes.form, {
          replace: true,
          state: restoredState,
        });
        return;
      }

      navigate(flowRoutes.entry);
      return;
    }

    const latestDraft = getLatestMatchedDraft();

    writeDraft({
      meta: flowMeta,
      currentStep: 'form1',
      form1: latestDraft?.form1,
      form21: null,
      form22: null,
    });
  }, [location.state?.phone, navigate, pageState?.phone, pageState?.lendela_app_id, pageState?.no_doc_loan_data, restoredState?.phone]);

  useEffect(() => {
    setValue('phone_number', fallbackPhone, { shouldValidate: true });
  }, [fallbackPhone, setValue]);

  useEffect(() => {
    if (hasFixedLoanAmount) {
      // disabled input은 react-hook-form 제출값에서 빠질 수 있으므로 폼 상태에도 고정값을 명시한다.
      setValue('loan_amount', numberWithCommas(fixedLoanAmount), { shouldValidate: true });
    }
  }, [fixedLoanAmount, hasFixedLoanAmount, setValue]);

  useEffect(() => {
    const latestDraft = getLatestMatchedDraft();

    if (latestDraft?.form1) {
      hydrateForm1Draft(latestDraft.form1);
    }
  }, [matchedDraft?.updatedAt, addressConfig]);

  const getDistrictOptionsByArea = (area, source = addressConfig) => {
    switch (area) {
      case 'HK':
        return source?.district_hk ?? [];
      case 'KLN':
        return source?.district_kw ?? [];
      case 'NT':
        return source?.district_nt ?? [];
      default:
        return [];
    }
  };

  const selectArea = (areaOption, { target = 'home' } = {}) => {
    if (target === 'work') {
      setWorkAreaDefault(areaOption);
      setWorkDistrictOptions(getDistrictOptionsByArea(areaOption?.value));
      setWorkDistrictValue(null);
      setValue('work_area', areaOption?.value ?? null);
      setValue('work_district', null);
      setError('work_area', null);
      return;
    }

    setAreaDefault(areaOption);
    setDistrictOptions(getDistrictOptionsByArea(areaOption?.value));
    setDistrictValue(null);
    setValue('area', areaOption?.value ?? null);
    setValue('district', null);
    setError('area', null);
  };

  const selectDistrict = (districtOption, { target = 'home' } = {}) => {
    if (target === 'work') {
      setWorkDistrictValue(districtOption);
      setValue('work_district', districtOption?.value ?? null);
      setError('work_district', null);
      return;
    }

    setDistrictValue(districtOption);
    setValue('district', districtOption?.value ?? null);
    setError('district', null);
  };

  const checkId = (hkid1, currentHkid2, hkid3) => {
    if (hkid1 && currentHkid2 && hkid3) {
      return validid.hkid(`${hkid1}${currentHkid2}/${hkid3}`);
    }
    return false;
  };

  const fillLendelaData = (data) => {
    setValue('loan_amount', numberWithCommas(data?.offer?.loanAmount));
    setValue('first_name', data?.firstName ?? '');
    setValue('last_name', data?.lastName ?? '');
    if (data?.personalNumber) {
      const hkid1 = data.personalNumber[0];
      const hkid3 = data.personalNumber[data.personalNumber.length - 1];
      const restoredHkid2 = data.personalNumber.slice(1, -1);
      setValue('hkid1', hkid1);
      setValue('hkid2', restoredHkid2);
      setHkid2(restoredHkid2);
      setValue('hkid3', hkid3);
    }
    if (data?.email) {
      setValue('email', data.email);
      setEmailDefault(data.email);
    }
    if (data?.loanPurpose) {
      const loanPurpose = options.loan_purpose.find((item) => item.lendela_value.includes(data.loanPurpose));
      const fallbackLoanPurpose = options.loan_purpose.find((item) => item.lendela_value.includes('other'));
      const nextLoanPurpose = loanPurpose ?? fallbackLoanPurpose;

      if (nextLoanPurpose) {
        setValue('loan_purpose', nextLoanPurpose.value);
        setLoanPurposeDefault(nextLoanPurpose);
      }
    }
    if (data?.residentialStatus) {
      const owner = RESI_OWNER_OPTIONS.find((item) => {
        if (data.residentialStatus === 'rented') {
          return item.value === '06';
        }
        if (data.residentialStatus === 'self-owned') {
          return item.value === '01';
        }
        if (data.residentialStatus === 'relative-owned') {
          return item.value === '03';
        }
        if (data.residentialStatus === 'jointly-owned') {
          return item.value === '02';
        }
        return false;
      });

      if (owner) {
        setValue('resi_own_sep', owner.value);
        setResiOwnerDefault(owner);
      }
    }
  };

  const fillNoDocLoanData = (data) => {
    setValue('loan_amount', numberWithCommas(data?.loan_amount));
    setAmountDefault(numberWithCommas(data?.loan_amount));
    setValue('first_name', data?.first_name ?? '');
    setValue('last_name', data?.last_name ?? '');
    if (data?.hkid) {
      const hkid1 = data.hkid[0];
      const hkid3 = data.hkid[data.hkid.length - 1];
      const restoredHkid2 = data.hkid.slice(1, -1);
      setValue('hkid1', hkid1);
      setValue('hkid2', restoredHkid2);
      setHkid2(restoredHkid2);
      setValue('hkid3', hkid3);
    }
    if (data?.email) {
      setValue('email', data.email);
      setEmailDefault(data.email);
    }
    if (data?.loan_purpose) {
      const loanPurpose = options.loan_purpose.find((item) => item.value === data.loan_purpose);
      if (loanPurpose) {
        setValue('loan_purpose', loanPurpose.value);
        setLoanPurposeDefault(loanPurpose);
      }
    }
    if (data?.date_of_birth) {
      const year = data.date_of_birth.substring(0, 4);
      const month = data.date_of_birth.substring(4, 6);
      const day = data.date_of_birth.substring(6, 8);
      setDateOfBirthDefault(`${year}/${month}/${day}`);
    }
    if (data?.education) {
      const education = options.education.find((item) => item.value === data.education);
      if (education) {
        setEducationDefault(education);
        setValue('education', data.education);
      }
    }
    if (data?.marriage_status) {
      const marriageStatus = options.marriage_status.find((item) => item.value === data.marriage_status);
      if (marriageStatus) {
        setMarriageStatusDefault(marriageStatus);
        setValue('marriage_status', data.marriage_status);
      }
    }
    if (data?.cust_gender) {
      const restoredGender = options.genders.find((item) => item.value === parseInt(data.cust_gender.replace('0', ''), 10));
      if (restoredGender) {
        setGender(restoredGender.value);
        setValue('gender', restoredGender.value);
        setGenderDefault(restoredGender);
      }
    }
  };

  const setSelectDefault = (value, list, setter, fieldName) => {
    if (value === undefined || value === null || value === '') {
      return;
    }

    const option = list.find((item) => item.value === value);
    if (option) {
      setter(option);
      setValue(fieldName, value);
    }
  };

  const setOptionalDateDefault = (dateValue, setter, fields) => {
    const selectedDate = formatSelectedDate(dateValue);
    if (!selectedDate) {
      return;
    }

    const normalizedDate = normalizeDateValue(dateValue);
    setter(selectedDate);
    setValue(fields.year, normalizedDate.substring(0, 4));
    setValue(fields.month, String(parseInt(normalizedDate.substring(4, 6), 10)));
    setValue(fields.day, String(parseInt(normalizedDate.substring(6, 8), 10)));
  };

  const hydrateForm1Draft = (data) => {
    if (!data) {
      return;
    }

    if (hasFixedLoanAmount) {
      setAmountDefault(fixedLoanAmount);
      setValue('loan_amount', numberWithCommas(fixedLoanAmount));
    } else if (data.loan_amount) {
      setAmountDefault(data.loan_amount);
      setValue('loan_amount', numberWithCommas(data.loan_amount));
    }
    if (data.apply_term !== undefined && data.apply_term !== null && data.apply_term !== '') {
      const applyTermOption = applyTermOptions.find((item) => String(item.value) === String(data.apply_term));
      if (applyTermOption) {
        setApplyTermDefault(applyTermOption);
        setValue('apply_term', applyTermOption.value);
      }
    }
    if (data.loan_purpose) {
      setSelectDefault(data.loan_purpose, options.loan_purpose, setLoanPurposeDefault, 'loan_purpose');
    }
    if (data.first_name) {
      setValue('first_name', data.first_name);
    }
    if (data.last_name) {
      setValue('last_name', data.last_name);
    }
    if (data.ref_recommender_no !== undefined) {
      setValue('ref_recommender_no', data.ref_recommender_no ?? '');
    }
    if (data.hkid_array?.length === 3) {
      setValue('hkid1', data.hkid_array[0]);
      setValue('hkid2', data.hkid_array[1]);
      setHkid2(data.hkid_array[1] ?? '');
      setValue('hkid3', data.hkid_array[2]);
    } else if (data.hkid?.length) {
      const restoredHkid1 = data.hkid[0];
      const restoredHkid3 = data.hkid[data.hkid.length - 1];
      const restoredHkid2 = data.hkid.slice(1, -1);
      setValue('hkid1', restoredHkid1);
      setValue('hkid2', restoredHkid2);
      setHkid2(restoredHkid2);
      setValue('hkid3', restoredHkid3);
    }
    if (data.date_of_birth?.length === 8) {
      const year = data.date_of_birth.substring(0, 4);
      const month = data.date_of_birth.substring(4, 6);
      const day = data.date_of_birth.substring(6, 8);
      setDateOfBirthDefault(`${year}/${month}/${day}`);
    }
    if (data.email) {
      setEmailDefault(data.email);
      setValue('email', data.email);
    }
    if (data.education) {
      setSelectDefault(data.education, options.education, setEducationDefault, 'education');
    }
    if (data.marriage_status) {
      setSelectDefault(data.marriage_status, options.marriage_status, setMarriageStatusDefault, 'marriage_status');
    }
    if (data.cust_gender) {
      const restoredGender = options.genders.find((item) => item.value === parseInt(data.cust_gender.replace('0', ''), 10));
      if (restoredGender) {
        setGender(restoredGender.value);
        setValue('gender', restoredGender.value);
        setGenderDefault(restoredGender);
      }
    }
    if (data.area) {
      const areaOption = (addressConfig?.area ?? options.area).find((item) => item.value === data.area);
      if (areaOption) {
        setAreaDefault(areaOption);
        setValue('area', data.area);
        const nextDistrictOptions = getDistrictOptionsByArea(data.area);
        setDistrictOptions(nextDistrictOptions);
        const nextDistrict = nextDistrictOptions.find((item) => item.value === data.district_value);
        if (nextDistrict) {
          setDistrictValue(nextDistrict);
          setValue('district', nextDistrict.value);
        }
      }
    }
    if (data.addr_line1_room !== undefined) {
      setValue('addr_line1_room', data.addr_line1_room ?? '');
    }
    if (data.addr_line1_floor !== undefined) {
      setValue('addr_line1_floor', data.addr_line1_floor ?? '');
    }
    if (data.addr_line1_block !== undefined) {
      setValue('addr_line1_block', data.addr_line1_block ?? '');
    }
    if (data.estate || data.addr_line2) {
      const estateValue = data.estate ?? data.addr_line2;
      setEstate(estateValue);
      setValue('estate', estateValue);
    }
    if (data.check_box_1 !== undefined) {
      setValue('checkbox1', !!data.check_box_1);
    }
    if (data.check_box_2 !== undefined) {
      setValue('checkbox2', !!data.check_box_2);
    }
    if (data.optional_expanded !== undefined) {
      setOptionalExpanded(!!data.optional_expanded);
    }
    if (data.messenger_id) {
      setValue('messenger_id', data.messenger_id);
    }
    if (data.work_name) {
      setValue('work_name', data.work_name);
    }
    if (data.work_area) {
      const areaOption = (addressConfig?.area ?? options.area).find((item) => item.value === data.work_area);
      if (areaOption) {
        setWorkAreaDefault(areaOption);
        setValue('work_area', data.work_area);
        const nextDistrictOptions = getDistrictOptionsByArea(data.work_area);
        setWorkDistrictOptions(nextDistrictOptions);
        const nextDistrict = nextDistrictOptions.find((item) => item.value === data.work_district);
        if (nextDistrict) {
          setWorkDistrictValue(nextDistrict);
          setValue('work_district', nextDistrict.value);
        }
      }
    }
    ['work_addr_line1_room', 'work_addr_line1_floor', 'work_addr_line1_block', 'work_addr_line2', 'work_tel_no2', 'home_tel_no'].forEach((fieldName) => {
      if (data[fieldName] !== undefined) {
        setValue(fieldName, data[fieldName] ?? '');
      }
    });
    setOptionalDateDefault(data.join_date, setJoinDateDefault, { year: 'join_date3', month: 'join_date2', day: 'join_date1' });
    setOptionalDateDefault(data.move_in_date, setMoveInDateDefault, { year: 'move_in_date3', month: 'move_in_date2', day: 'move_in_date1' });
    setSelectDefault(data.pay_day, PAY_DAY_OPTIONS, setPayDayDefault, 'pay_day');
    setSelectDefault(data.resi_own_sep, RESI_OWNER_OPTIONS, setResiOwnerDefault, 'resi_own_sep');
    setSelectDefault(data.number_living_with, options.number_living_with, setNumberLivingWithDefault, 'number_living_with');
    if (data.monthly_rent !== undefined && data.monthly_rent !== null && data.monthly_rent !== '') {
      const nextMonthlyRent = numberWithCommas(data.monthly_rent);
      setMonthlyRent(nextMonthlyRent);
      setValue('monthly_rent', nextMonthlyRent);
    }

    setCheckBoxTime({
      check_box_1_time: data.check_box_1_time ? new Date(data.check_box_1_time) : null,
      check_box_2_time: data.check_box_2_time ? new Date(data.check_box_2_time) : null,
    });
  };

  const buildWorkAddress = (currentValues) => {
    const workArea = (addressConfig?.area ?? options.area).find((item) => item.value === currentValues?.work_area);
    const workDistrict = getDistrictOptionsByArea(currentValues?.work_area).find((item) => item.value === currentValues?.work_district);
    const workAddressLine = buildAddressLine1(currentValues?.work_addr_line1_room, currentValues?.work_addr_line1_floor, currentValues?.work_addr_line1_block);

    return [workArea?.label, workDistrict?.label, workAddressLine, currentValues?.work_addr_line2].filter(Boolean).join(' ').trim();
  };

  const buildForm1DraftSnapshot = (baseData = {}) => {
    const currentValues = getValues();
    const currentHkid1 = currentValues?.hkid1 ?? baseData?.hkid_array?.[0] ?? baseData?.hkid?.[0] ?? '';
    const currentHkid2 = (hkid2 ?? '').trim() || currentValues?.hkid2 || baseData?.hkid_array?.[1] || baseData?.hkid?.slice?.(1, -1) || '';
    const currentHkid3 = currentValues?.hkid3 ?? baseData?.hkid_array?.[2] ?? baseData?.hkid?.[baseData?.hkid?.length - 1] ?? '';
    const currentDateYear = currentValues?.date_of_birth3 ?? (baseData?.date_of_birth ? baseData.date_of_birth.substring(0, 4) : '');
    const currentDateMonth = currentValues?.date_of_birth2 ?? (baseData?.date_of_birth ? String(parseInt(baseData.date_of_birth.substring(4, 6), 10)) : '');
    const currentDateDay = currentValues?.date_of_birth1 ?? (baseData?.date_of_birth ? String(parseInt(baseData.date_of_birth.substring(6, 8), 10)) : '');
    const currentJoinDate = formatDateParts(currentValues?.join_date3, currentValues?.join_date2, currentValues?.join_date1) || baseData?.join_date || '';
    const currentMoveInDate = formatDateParts(currentValues?.move_in_date3, currentValues?.move_in_date2, currentValues?.move_in_date1) || baseData?.move_in_date || '';
    const currentWorkAreaValue = currentValues?.work_area ?? baseData?.work_area;
    const currentWorkDistrictValue = currentValues?.work_district ?? baseData?.work_district;
    const currentWorkProvince = (addressConfig?.area ?? options.area).find((item) => item.value === currentWorkAreaValue);
    const currentWorkDistrict = getDistrictOptionsByArea(currentWorkAreaValue).find((item) => item.value === currentWorkDistrictValue);

    return {
      ...baseData,
      // Quick Cash에서는 초안에 이전 값이 남아 있어도 상품의 고정 금액을 우선한다.
      loan_amount: hasFixedLoanAmount
        ? String(fixedLoanAmount)
        : (currentValues?.loan_amount ? numberWithNoCommas(currentValues.loan_amount) : (baseData?.loan_amount ?? '')),
      apply_term: currentValues?.apply_term ?? baseData?.apply_term,
      loan_purpose: currentValues?.loan_purpose ?? baseData?.loan_purpose,
      first_name: currentValues?.first_name ?? baseData?.first_name ?? '',
      last_name: currentValues?.last_name ?? baseData?.last_name ?? '',
      ref_recommender_no: currentValues?.ref_recommender_no ?? baseData?.ref_recommender_no ?? '',
      cust_gender: currentValues?.gender ? `0${currentValues.gender}` : (baseData?.cust_gender ?? (gender ? `0${gender}` : undefined)),
      hkid: currentHkid1 && currentHkid2 && currentHkid3 ? `${currentHkid1}${currentHkid2}${currentHkid3}` : baseData?.hkid,
      hkid_array: [currentHkid1, currentHkid2, currentHkid3],
      date_of_birth: currentDateYear && currentDateMonth && currentDateDay ? `${currentDateYear}${pad(currentDateMonth)}${pad(currentDateDay)}` : baseData?.date_of_birth,
      phone_number: fallbackPhone,
      email: currentValues?.email ?? baseData?.email ?? '',
      education: currentValues?.education ?? baseData?.education,
      marriage_status: currentValues?.marriage_status ?? baseData?.marriage_status,
      area: currentValues?.area ?? baseData?.area,
      district_value: currentValues?.district ?? districtValue?.value ?? baseData?.district_value,
      addr_line1_room: currentValues?.addr_line1_room ?? baseData?.addr_line1_room ?? '',
      addr_line1_floor: currentValues?.addr_line1_floor ?? baseData?.addr_line1_floor ?? '',
      addr_line1_block: currentValues?.addr_line1_block ?? baseData?.addr_line1_block ?? '',
      estate: currentValues?.estate ?? estate ?? baseData?.estate ?? '',
      addr_line2: currentValues?.estate ?? estate ?? baseData?.addr_line2 ?? '',
      check_box_1: currentValues?.checkbox1 ?? baseData?.check_box_1 ?? false,
      check_box_2: currentValues?.checkbox2 ?? baseData?.check_box_2 ?? false,
      check_box_1_time: checkBoxTime?.check_box_1_time ? moment(checkBoxTime.check_box_1_time).format('YYYY-MM-DD HH:mm:ss') : (baseData?.check_box_1_time ?? null),
      check_box_2_time: checkBoxTime?.check_box_2_time ? moment(checkBoxTime.check_box_2_time).format('YYYY-MM-DD HH:mm:ss') : (baseData?.check_box_2_time ?? null),
      optional_expanded: optionalExpanded,
      messenger_id: currentValues?.messenger_id ?? baseData?.messenger_id ?? '',
      work_name: currentValues?.work_name ?? baseData?.work_name ?? '',
      work_area: currentWorkAreaValue,
      work_district: currentWorkDistrictValue,
      work_province_id: currentWorkProvince?.province_id ?? baseData?.work_province_id,
      work_city_id: currentWorkDistrict?.city_id ?? baseData?.work_city_id,
      work_city_id_tu: currentWorkDistrict?.tu_value ?? baseData?.work_city_id_tu,
      work_addr_line1_room: currentValues?.work_addr_line1_room ?? baseData?.work_addr_line1_room ?? '',
      work_addr_line1_floor: currentValues?.work_addr_line1_floor ?? baseData?.work_addr_line1_floor ?? '',
      work_addr_line1_block: currentValues?.work_addr_line1_block ?? baseData?.work_addr_line1_block ?? '',
      work_addr_line2: currentValues?.work_addr_line2 ?? baseData?.work_addr_line2 ?? '',
      work_tel_no2: currentValues?.work_tel_no2 ?? baseData?.work_tel_no2 ?? '',
      join_date: currentJoinDate,
      pay_day: currentValues?.pay_day ?? baseData?.pay_day,
      resi_own_sep: currentValues?.resi_own_sep ?? baseData?.resi_own_sep,
      monthly_rent: currentValues?.monthly_rent ? numberWithNoCommas(currentValues.monthly_rent) : (baseData?.monthly_rent ?? ''),
      number_living_with: currentValues?.number_living_with ?? baseData?.number_living_with,
      move_in_date: currentMoveInDate,
      home_tel_no: currentValues?.home_tel_no ?? baseData?.home_tel_no ?? baseData?.resi_tel_no2 ?? '',
    };
  };

  const persistCurrentForm1Draft = (currentStep = 'form1') => {
    if (!flowMeta?.phone) {
      return null;
    }

    const latestDraft = getLatestMatchedDraft();
    const snapshot = buildForm1DraftSnapshot(latestDraft?.form1 ?? {});

    writeDraft({
      meta: flowMeta,
      currentStep,
      form1: snapshot,
      form21: null,
      form22: null,
    });

    return snapshot;
  };

  const navigateToPreviousStep = ({ replace = false, useBrowserHistory = false } = {}) => {
    const snapshot = persistCurrentForm1Draft('form1');
    const previousRoute = buildApplicationFlowPreviousRoute({
      step: APPLICATION_FLOW_STEP.FORM1,
      flowMeta,
      draft: snapshot ?? getLatestMatchedDraft(),
      routes: flowRoutes,
    });

    if (!previousRoute) {
      return;
    }

    if (useBrowserHistory && canMoveApplicationFlowHistoryBack()) {
      navigate(-1);
      return;
    }

    navigate(previousRoute.pathname, {
      replace,
      state: previousRoute.state,
    });
  };

  const onClickPrevious = () => {
    navigateToPreviousStep({ useBrowserHistory: true });
  };

  const refRecommenderNoField = register('ref_recommender_no', {
    validate: isValidRefRecommenderNo,
  });

  const workNameField = register('work_name', {
    maxLength: 20,
    validate: (value) => !value || companyNameOnly(value) === value,
  });

  const messengerIdField = register('messenger_id', {
    validate: (value) => isOptionalMaxLength(value, 20),
    onChange: (e) => {
      e.target.value = (e?.target?.value || '').slice(0, 20);
      setValue('messenger_id', e.target.value, { shouldValidate: true });
      if (isOptionalMaxLength(e.target.value, 20)) {
        clearErrors('messenger_id');
      }
    },
  });

  const workPhoneField = register('work_tel_no2', {
    validate: (value) => isOptionalHongKongPhone(value),
    onChange: (e) => {
      e.target.value = phoneNumberOnly(e?.target?.value).slice(0, 8);
      setValue('work_tel_no2', e.target.value, { shouldValidate: true });
      if (isOptionalHongKongPhone(e.target.value)) {
        clearErrors('work_tel_no2');
      }
    },
  });

  const homePhoneField = register('home_tel_no', {
    validate: (value) => isOptionalHongKongPhone(value),
    onChange: (e) => {
      e.target.value = phoneNumberOnly(e?.target?.value).slice(0, 8);
      setValue('home_tel_no', e.target.value, { shouldValidate: true });
      if (isOptionalHongKongPhone(e.target.value)) {
        clearErrors('home_tel_no');
      }
    },
  });

  useEffect(() => {
    const beforeunload = (e) => {
      persistCurrentForm1Draft('form1');
      const event = e || window.event;
      if (event) {
        event.returnValue = 'Sure?';
      }
      return 'Sure?';
    };

    let currentHistoryIndex = getApplicationFlowHistoryIndex();

    const handlePopState = (event) => {
      const shouldMoveToPreviousStep = isApplicationFlowBackPopState(event, currentHistoryIndex);
      currentHistoryIndex = getApplicationFlowHistoryIndex();

      if (shouldMoveToPreviousStep) {
        navigateToPreviousStep({ replace: true });
        return;
      }

      persistCurrentForm1Draft('form1');
    };

    window.addEventListener('beforeunload', beforeunload);
    window.addEventListener('popstate', handlePopState, true);

    return () => {
      window.removeEventListener('beforeunload', beforeunload);
      window.removeEventListener('popstate', handlePopState, true);
    };
  }, [addressConfig, checkBoxTime, fallbackPhone, flowMeta, hkid2, matchedDraft?.form1, navigateToPreviousStep]);

  const onSubmit = async (data) => {
    setErrorMessage(null);

    if (!checkId(data?.hkid1, data?.hkid2, data?.hkid3)) {
      setFocus('hkid3');
      return;
    }

    const selectedDistrictValue = districtValue?.value ?? data?.district;
    const selectedDistrict = getDistrictOptionsByArea(data?.area).find((item) => item.value === selectedDistrictValue);
    const selectedProvince = addressConfig?.area?.find((item) => item.value === data?.area);
    const selectedWorkArea = data?.work_area;
    const selectedWorkDistrictValue = data?.work_district;
    const selectedWorkDistrict = getDistrictOptionsByArea(selectedWorkArea).find((item) => item.value === selectedWorkDistrictValue);
    const selectedWorkProvince = (addressConfig?.area ?? options.area).find((item) => item.value === selectedWorkArea);
    const addrLine1 = buildAddressLine1(data?.addr_line1_room, data?.addr_line1_floor, data?.addr_line1_block);
    const workAddress = buildWorkAddress(data);

    if (addrLine1.length > 49 || data?.estate?.length > 49 || workAddress.length > 49) {
      setErrorMessage('輸入的地址不可以超過50個字元');
      window.scrollTo(0, 0);
      return;
    }

    const responseDoc = await dispatch(actionGetStateDoc());
    const agreeDoc = (responseDoc?.content ?? []).map((doc) => ({
      doc_type: doc.doc_type,
      doc_version: doc.doc_ver,
    }));
    const responseIP = await dispatch(actionApplicationIP());
    const currentGender = data?.gender ?? gender;

    const normalizedMonthlyRent = data?.monthly_rent ? numberWithNoCommas(data.monthly_rent) : '';
    const joinDate = formatDateParts(data?.join_date3, data?.join_date2, data?.join_date1);
    const moveInDate = formatDateParts(data?.move_in_date3, data?.move_in_date2, data?.move_in_date1);
    const form1Draft = {
      ...buildForm1DraftSnapshot(),
      first_name: data?.first_name,
      last_name: data?.last_name,
      phone_number: data?.phone_number ?? fallbackPhone,
      hkid: `${data?.hkid1}${data?.hkid2}${data?.hkid3}`,
      hkid_array: [data?.hkid1, data?.hkid2, data?.hkid3],
      date_of_birth: `${data?.date_of_birth3}${pad(data?.date_of_birth2)}${pad(data?.date_of_birth1)}`,
      cust_gender: `0${currentGender}`,
      ref_recommender_no: data?.ref_recommender_no ?? '',
      loan_amount: hasFixedLoanAmount ? String(fixedLoanAmount) : numberWithNoCommas(data?.loan_amount),
      apply_term: data?.apply_term,
      loan_purpose: data?.loan_purpose,
      email: data?.email,
      education: data?.education,
      marriage_status: data?.marriage_status,
      area: data?.area,
      province_id: selectedProvince?.province_id,
      city_id: selectedDistrict?.city_id,
      city_id_tu: selectedDistrict?.tu_value,
      district_value: selectedDistrictValue,
      addr_line1: addrLine1,
      addr_line1_room: data?.addr_line1_room,
      addr_line1_floor: data?.addr_line1_floor ?? '',
      addr_line1_block: data?.addr_line1_block ?? '',
      addr_line2: data?.estate,
      estate: data?.estate,
      address_config: addressConfig,
      messenger_id: data?.messenger_id,
      work_name: data?.work_name,
      work_addr: workAddress,
      work_area: data?.work_area,
      work_district: data?.work_district,
      work_province_id: selectedWorkProvince?.province_id,
      work_city_id: selectedWorkDistrict?.city_id,
      work_city_id_tu: selectedWorkDistrict?.tu_value,
      work_addr_line1_room: data?.work_addr_line1_room ?? '',
      work_addr_line1_floor: data?.work_addr_line1_floor ?? '',
      work_addr_line1_block: data?.work_addr_line1_block ?? '',
      work_addr_line2: data?.work_addr_line2 ?? '',
      work_tel_no2: data?.work_tel_no2,
      join_date: joinDate,
      pay_day: data?.pay_day,
      resi_own_sep: data?.resi_own_sep,
      monthly_rent: normalizedMonthlyRent,
      number_living_with: data?.number_living_with,
      move_in_date: moveInDate,
      home_tel_no: data?.home_tel_no,
      resi_tel_no2: data?.home_tel_no,
      check_box_1: data?.checkbox1,
      check_box_2: data?.checkbox2,
      check_box_1_time: moment(checkBoxTime?.check_box_1_time).format('YYYY-MM-DD HH:mm:ss'),
      check_box_2_time: moment(checkBoxTime?.check_box_2_time).format('YYYY-MM-DD HH:mm:ss'),
      agree_doc: JSON.stringify(agreeDoc),
      application_device: detectMob() ? 2 : 1,
      application_ip: responseIP?.ip,
      application_browser: window.navigator.userAgent,
      application_form1_merged: true,
    };

    writeDraft({
      meta: flowMeta,
      currentStep: 'confirm',
      form1: form1Draft,
      form21: null,
      form22: null,
    });

    navigate(flowRoutes.confirm, {
      state: {
        status: true,
        form1: form1Draft,
        lendela_data: lendelaData,
        source: 'applicationForm1Merge',
      },
    });
  };

  const renderSelect = ({
    title,
    description,
    field,
    required = false,
    value,
    setter,
    selectOptions,
    disabled,
    onChange,
    titleClassName = 'text-xl text-gray mb-2 font-bold',
  }) => (
    <div className="mb-6">
      <p className={titleClassName}>{title}</p>
      {description && <p className="text-xs text-gray1 mb-2">{description}</p>}
      <div className="border-b border-primary relative">
        <Select
          placeholder="- 請選擇 -"
          className="w-full"
          theme={(theme) => ({
            ...theme,
            borderRadius: 0,
          })}
          styles={styles}
          {...register(field, { required })}
          onChange={(selectedOption) => {
            setValue(field, selectedOption?.value);
            setError(field, null);
            if (setter) {
              setter(selectedOption);
            }
            if (onChange) {
              onChange(selectedOption);
            }
          }}
          options={selectOptions}
          isSearchable={false}
          components={{ DropdownIndicator }}
          value={value}
          isDisabled={disabled}
        />
      </div>
      {errors?.[field] && errors?.[field]?.type === 'required' && <span className="text-10 text-red-500">*必填</span>}
    </div>
  );

  if (!location.state?.phone && restoredState?.phone) {
    return null;
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {isVisibleInvalidToken && <PopUpBox.InvalidToken />}
      <Common.Loading isVisible={isSubmitting} />
      {errorMessage && (
        <PopUpBox.Error content={errorMessage} onClick={() => setErrorMessage(null)}>
          <div className="text-center my-3">
            <button onClick={() => setErrorMessage(null)} type="button" className="rounded-full text-white text-sm bg-lightningYellow px-14 py-2">
              確定
            </button>
          </div>
        </PopUpBox.Error>
      )}
      <div className="bg-white">
        <div className="px-5 pt-5 pb-0">
          <Common.StepBar active={1} />
        </div>

        <div className="px-5 pt-0 pb-5">
          <div className="mb-6">
            <p className="text-xl text-gray mb-2 font-bold">貸款金額</p>
            <Common.InputMoney
              defaultValue={hasFixedLoanAmount ? fixedLoanAmount : (lendelaData?.offer?.loanAmount || amountDefault)}
              setValue={setValue}
              disabled={hasFixedLoanAmount || !!lendelaData}
              register={register}
              title="loan_amount"
              setError={setError}
              errors={errors}
            />
          </div>

          {applyTermOptions.length > 0 && renderSelect({
            title: '貸款期數',
            description: '*實際獲批貸款期數視乎申請人之償還能而定',
            field: 'apply_term',
            required: true,
            value: applyTermDefault,
            setter: setApplyTermDefault,
            selectOptions: applyTermOptions,
          })}

          <div className="mb-6">
            <p className="text-xl font-bold text-gray">英文姓名</p>
            <p className="text-xs text-gray1 mb-2">需與身份證上的名字相同</p>
            <div className="flex border-b border-primary mb-2">
              <p className="text-gray1 mb-1">姓氏：</p>
              <input
                disabled={!!lendelaData}
                {...register('last_name', {
                  required: true,
                  onChange: (e) => {
                    const value = alphabetOnly(e?.target?.value + '');
                    setValue('last_name', value.toUpperCase(), { shouldValidate: true });
                  },
                })}
                className="flex-1 px-2 outline-none text-primary"
              />
            </div>
            {errors?.last_name?.type === 'required' && <span className="text-10 text-red-500">*必填</span>}
            <div className="flex border-b border-primary mb-4">
              <p className="text-gray1 mb-1">名字：</p>
              <input
                disabled={!!lendelaData}
                {...register('first_name', {
                  required: true,
                  onChange: (e) => {
                    const value = alphabetOnly(e?.target?.value + '');
                    setValue('first_name', capitalizeName(value), { shouldValidate: true });
                  },
                })}
                className="flex-1 px-2 outline-none text-primary"
              />
            </div>
            {errors?.first_name?.type === 'required' && <span className="text-10 text-red-500">*必填</span>}
          </div>

          {renderSelect({
            title: '性別',
            field: 'gender',
            required: true,
            value: genderDefault,
            setter: (selectedOption) => {
              setGender(selectedOption?.value);
              setGenderDefault(selectedOption);
            },
            selectOptions: options.genders,
            titleClassName: 'text-xl text-gray mb-2 font-bold',
          })}

          <div className="mb-6">
            <p className="text-xl font-bold text-gray mb-2">香港身份證號碼</p>
            <div className="flex items-center">
              <div className="border-b w-1/3 border-primary">
                <input
                  disabled={!!lendelaData}
                  tabIndex={-1}
                  maxLength={2}
                  {...register('hkid1', {
                    required: true,
                    onChange: (e) => {
                      setValue('hkid1', alphabetOnly(e.target.value.toUpperCase().replace(' ', '')), { shouldValidate: true });
                    },
                  })}
                  className="px-2 outline-none text-primary w-full text-center"
                  placeholder="A"
                />
              </div>
              <div className="border-b w-full border-primary ml-2 mr-2">
                <input
                  disabled={!!lendelaData}
                  tabIndex={-1}
                  maxLength={6}
                  {...register('hkid2', { required: true })}
                  value={hkid2}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, '');
                    setHkid2(value);
                    setValue('hkid2', value, { shouldValidate: true });
                  }}
                  className="px-2 outline-none text-primary w-full text-center"
                  placeholder="123456"
                  inputMode="numeric"
                />
              </div>
              <div className="border-b border-primary text-center">
                <span className="text-primary">(</span>
                <input
                  disabled={!!lendelaData}
                  tabIndex={-1}
                  maxLength={1}
                  {...register('hkid3', {
                    required: true,
                    onChange: (e) => {
                      setValue('hkid3', alphabetAndNumberOnly(e.target.value.toUpperCase().replace(' ', '')), { shouldValidate: true });
                    },
                  })}
                  className="px-1 outline-none text-primary w-3/4 text-center"
                  placeholder="3"
                />
                <span className="text-primary">)</span>
              </div>
            </div>
            {(errors?.hkid1 || errors?.hkid2 || errors?.hkid3) &&
              (errors?.hkid1?.type === 'required' || errors?.hkid2?.type === 'required' || errors?.hkid3?.type === 'required') && <span className="text-10 text-red-500">*必填</span>}
            {(getValues('hkid1') && getValues('hkid2') && getValues('hkid3') && !checkId(getValues('hkid1'), getValues('hkid2'), getValues('hkid3'))) &&
              (!(errors?.hkid1 || errors?.hkid2 || errors?.hkid3) ||
                !(errors?.hkid1?.type === 'required' || errors?.hkid2?.type === 'required' || errors?.hkid3?.type === 'required')) && (
                <span className="text-sm text-red-500">*請輸入正確的身份證號碼</span>
              )}
          </div>

          <div className="mb-6">
            <p className="text-xl text-gray mb-2 font-semibold">出生日期</p>
            <Common.InputDate disabled={!!lendelaData} selectedDate={lendelaData?.dateOfBirth?.replace(/-/g, '/') || dateOfBirthDefault} register={register} errors={errors} getValues={getValues} setValue={setValue} setError={setError} />
          </div>

          <div className="mb-6">
            <p className="text-xl text-gray mb-2 font-semibold">手提電話</p>
            <div className="flex items-center border-b border-primary">
              <input value={fallbackPhone} readOnly {...register('phone_number', { required: true })} className="flex-1 px-2 outline-none w-full bg-white text-primary" />
            </div>
            {errors?.phone_number?.type === 'required' && <span className="text-10 text-red-500">*必填</span>}
          </div>

          {renderSelect({
            title: '區域',
            field: 'area',
            required: true,
            value: areaDefault,
            selectOptions: addressConfig?.area ?? options.area,
            onChange: (selectedOption) => selectArea(selectedOption),
            titleClassName: 'text-gray font-bold mb-2',
          })}
          {renderSelect({
            title: '地區',
            field: 'district',
            required: true,
            value: districtValue,
            selectOptions: districtOptions,
            onChange: (selectedOption) => selectDistrict(selectedOption),
            titleClassName: 'text-gray font-bold mb-2',
          })}
          <div className="mb-5 flex items-center">
            <div className="flex flex-col pr-1">
              <p className="text-gray font-bold mb-2">單位</p>
              <div className="flex items-center border-b border-primary">
                <input
                  {...register('addr_line1_room', {
                    required: true,
                    onChange: (e) => {
                      setValue('addr_line1_room', alphabetAndNumberOnly((e?.target?.value || '')).toUpperCase(), { shouldValidate: true });
                    },
                  })}
                  className="flex-1 px-1 outline-none text-primary w-full"
                  placeholder="Room/Flat/Unit"
                />
              </div>
              {errors?.addr_line1_room?.type === 'required' && <span className="text-10 text-red-500">*必填</span>}
            </div>
            <div className="flex flex-col px-1">
              <p className="text-gray font-bold mb-2">樓層</p>
              <div className="flex items-center border-b border-primary">
                <input
                  {...register('addr_line1_floor', {
                    onChange: (e) => {
                      setValue('addr_line1_floor', alphabetAndNumberOnly((e?.target?.value || '')).toUpperCase(), { shouldValidate: true });
                    },
                  })}
                  inputMode="numeric"
                  className="flex-1 px-1 outline-none text-primary w-full"
                  placeholder="Floor"
                />
              </div>
            </div>
            <div className="flex flex-col px-1">
              <p className="text-gray font-bold mb-2">座數/大廈</p>
              <div className="flex items-center border-b border-primary">
                <input
                  {...register('addr_line1_block', {
                    onChange: (e) => {
                      setValue('addr_line1_block', alphabetAndNumberOnly((e?.target?.value || '')).toUpperCase(), { shouldValidate: true });
                    },
                  })}
                  className="flex-1 px-1 outline-none text-primary w-full"
                  placeholder="Block"
                />
              </div>
            </div>
          </div>
          <div className="mb-6">
            <p className="text-gray font-bold mb-2">屋苑/街道</p>
            <div className="flex items-center border-b border-primary">
              <input
                {...register('estate', { required: true })}
                value={estate}
                onChange={(e) => {
                  const value = alphabetAndNumberOnly((e?.target?.value || '')).toUpperCase();
                  setEstate(value);
                  setValue('estate', value, { shouldValidate: true });
                }}
                className="flex-1 px-2 outline-none text-primary w-full"
                placeholder="(E.g. HAPPY EST)"
              />
            </div>
            {errors?.estate?.type === 'required' && <span className="text-10 text-red-500">*必填</span>}
          </div>

          <div className="mb-6">
            <p className="text-xl text-gray mb-2 font-semibold">推薦碼 (如有)</p>
            <div className="flex items-center border-b border-primary">
              <input
                maxLength={6}
                {...refRecommenderNoField}
                onChange={(e) => {
                  e.target.value = normalizeRefRecommenderNo(e.target.value);
                  refRecommenderNoField.onChange(e);
                }}
                className="flex-1 px-2 outline-none w-full bg-white text-primary"
                placeholder="(請確認輸入的推薦碼無誤)"
              />
            </div>
            {errors?.ref_recommender_no?.type === 'validate' && <span className="text-10 text-red-500">推薦碼必須為6個字元 </span>}
          </div>

          <button
            type="button"
            onClick={() => setOptionalExpanded((prev) => !prev)}
            className="w-full min-h-[52px] bg-[#f4f4f4] text-gray rounded-full px-5 flex items-center justify-center gap-3 mb-8"
          >
            <span className="text-base leading-tight">選項項目（填寫可加快審批進度）</span>
            <span className="h-6 w-6 flex items-center justify-center text-primary">
              <FontAwesomeIcon className="block" icon={optionalExpanded ? faCaretUp : faCaretDown} size="lg" />
            </span>
          </button>

          {optionalExpanded && (
            <div className="mb-8">
              {renderSelect({
                title: '貸款目的',
                field: 'loan_purpose',
                value: loanPurposeDefault,
                setter: setLoanPurposeDefault,
                selectOptions: options.loan_purpose,
                disabled: !!loanPurposeDefault && !!lendelaData,
              })}
              {renderSelect({
                title: '學歷',
                field: 'education',
                value: educationDefault,
                setter: setEducationDefault,
                selectOptions: options.education,
                titleClassName: 'text-xl text-gray mb-2 font-semibold',
              })}
              {renderSelect({
                title: '婚姻狀態',
                field: 'marriage_status',
                value: marriageStatusDefault,
                setter: setMarriageStatusDefault,
                selectOptions: options.marriage_status,
                titleClassName: 'text-xl text-gray mb-2 font-semibold',
              })}
              <div className="mb-6">
                <p className="text-xl text-gray mb-2 font-semibold">電郵地址</p>
                <Common.InputEmail
                  register={register}
                  setValue={setValue}
                  onChange={(email) => setValue('email', email, { shouldValidate: true })}
                  getValues={getValues}
                  setError={setError}
                  selectedValue={lendelaData?.email || emailDefault}
                  disabled={!!lendelaData?.email}
                  required={false}
                  clearEmailNameOnDomainSelect={false}
                />
                <input type="hidden" {...register('email', { pattern: OPTIONAL_EMAIL_PATTERN })} />
                {errors?.email?.type === 'pattern' && <span className="text-10 text-red-500">*請輸入正確的電郵</span>}
              </div>
              <div className="mb-6">
                <p className="text-xl text-gray mb-2 font-semibold">Facebook/Instagram</p>
                <div className="flex items-center border-b border-primary">
                  <input maxLength={20} {...messengerIdField} className="flex-1 px-2 outline-none w-full bg-white text-primary" />
                </div>
                {errors?.messenger_id && <span className="text-10 text-red-500">*請輸入20個字元以內</span>}
              </div>
              <div className="mb-6">
                <p className="text-xl text-gray mb-2 font-semibold">公司名稱</p>
                <div className="flex items-center border-b border-primary">
                  <input
                    maxLength={20}
                    {...workNameField}
                    onChange={(e) => {
                      e.target.value = companyNameOnly(e?.target?.value || '').toUpperCase().slice(0, 20);
                      workNameField.onChange(e);
                      if (companyNameOnly(e.target.value) === e.target.value && e.target.value.length <= 20) {
                        clearErrors('work_name');
                      }
                    }}
                    className="flex-1 px-2 outline-none w-full bg-white text-primary"
                  />
                </div>
                {errors?.work_name && <span className="text-10 text-red-500">*請輸入正確的公司名稱</span>}
              </div>
              <div className="mb-6">
                <p className="text-xl text-gray mb-2 font-semibold">公司地址</p>
                {renderSelect({
                  title: '區域',
                  field: 'work_area',
                  value: workAreaDefault,
                  selectOptions: addressConfig?.area ?? options.area,
                  onChange: (selectedOption) => selectArea(selectedOption, { target: 'work' }),
                  titleClassName: 'text-gray font-bold mb-2',
                })}
                {renderSelect({
                  title: '地區',
                  field: 'work_district',
                  value: workDistrictValue,
                  selectOptions: workDistrictOptions,
                  onChange: (selectedOption) => selectDistrict(selectedOption, { target: 'work' }),
                  titleClassName: 'text-gray font-bold mb-2',
                })}
                <div className="mb-5 flex items-center">
                  <div className="flex flex-col pr-1">
                    <p className="text-gray font-bold mb-2">單位</p>
                    <div className="flex items-center border-b border-primary">
                      <input
                        {...register('work_addr_line1_room', {
                          onChange: (e) => {
                            setValue('work_addr_line1_room', alphabetAndNumberOnly((e?.target?.value || '')).toUpperCase());
                          },
                        })}
                        className="flex-1 px-1 outline-none text-primary w-full"
                        placeholder="Room/Flat/Unit"
                      />
                    </div>
                  </div>
                  <div className="flex flex-col px-1">
                    <p className="text-gray font-bold mb-2">樓層</p>
                    <div className="flex items-center border-b border-primary">
                      <input
                        {...register('work_addr_line1_floor', {
                          onChange: (e) => {
                            setValue('work_addr_line1_floor', alphabetAndNumberOnly((e?.target?.value || '')).toUpperCase());
                          },
                        })}
                        inputMode="numeric"
                        className="flex-1 px-1 outline-none text-primary w-full"
                        placeholder="Floor"
                      />
                    </div>
                  </div>
                  <div className="flex flex-col px-1">
                    <p className="text-gray font-bold mb-2">座數/大廈</p>
                    <div className="flex items-center border-b border-primary">
                      <input
                        {...register('work_addr_line1_block', {
                          onChange: (e) => {
                            setValue('work_addr_line1_block', alphabetAndNumberOnly((e?.target?.value || '')).toUpperCase());
                          },
                        })}
                        className="flex-1 px-1 outline-none text-primary w-full"
                        placeholder="Block"
                      />
                    </div>
                  </div>
                </div>
                <div className="mb-6">
                  <p className="text-gray font-bold mb-2">屋苑/街道</p>
                  <div className="flex items-center border-b border-primary">
                    <input
                      {...register('work_addr_line2', {
                        onChange: (e) => {
                          setValue('work_addr_line2', alphabetAndNumberOnly((e?.target?.value || '')).toUpperCase());
                        },
                      })}
                      className="flex-1 px-2 outline-none text-primary w-full"
                      placeholder="(E.g. HAPPY EST)"
                    />
                  </div>
                </div>
              </div>
              <div className="mb-6">
                <p className="text-xl text-gray mb-2 font-semibold">公司電話</p>
                <div className="flex items-center border-b border-primary">
                  <input
                    maxLength={8}
                    inputMode="numeric"
                    {...workPhoneField}
                    className="flex-1 px-2 outline-none w-full bg-white text-primary"
                    placeholder="(+852)"
                  />
                </div>
                {errors?.work_tel_no2 && <span className="text-10 text-red-500">*請輸入正確的電話號碼</span>}
              </div>
              <div className="mb-6">
                <p className="text-xl text-gray mb-2 font-semibold">入職日期</p>
                <Common.InputDate
                  selectedDate={joinDateDefault}
                  register={register}
                  errors={errors}
                  getValues={getValues}
                  setValue={setValue}
                  setError={setError}
                  fields={{ year: 'join_date3', month: 'join_date2', day: 'join_date1' }}
                  required={false}
                  ageLimit={false}
                  limitToToday={true}
                />
              </div>
              {renderSelect({
                title: '每月出糧日',
                field: 'pay_day',
                value: payDayDefault,
                setter: setPayDayDefault,
                selectOptions: PAY_DAY_OPTIONS,
                titleClassName: 'text-xl text-gray mb-2 font-semibold',
              })}
              {renderSelect({
                title: '住所持有人',
                field: 'resi_own_sep',
                value: resiOwnerDefault,
                setter: setResiOwnerDefault,
                selectOptions: RESI_OWNER_OPTIONS,
                titleClassName: 'text-xl text-gray mb-2 font-semibold',
              })}
              <div className="mb-6">
                <p className="text-xl text-gray mb-2 font-semibold">每月房租</p>
                <p className="text-xs text-primary">(如果申請人無須交租，可輸入0)</p>
                <Common.InputMoney
                  defaultValue={monthlyRent}
                  setValue={setValue}
                  zeroAllow={true}
                  placeholder=""
                  register={register}
                  title="monthly_rent"
                  setError={setError}
                  errors={errors}
                  required={false}
                  maxDigits={6}
                />
              </div>
              {renderSelect({
                title: '同住人數',
                field: 'number_living_with',
                value: numberLivingWithDefault,
                setter: setNumberLivingWithDefault,
                selectOptions: options.number_living_with,
                titleClassName: 'text-xl text-gray mb-2 font-semibold',
              })}
              <div className="mb-6">
                <p className="text-xl text-gray mb-2 font-semibold">入住日期</p>
                <Common.InputDate
                  selectedDate={moveInDateDefault}
                  register={register}
                  errors={errors}
                  getValues={getValues}
                  setValue={setValue}
                  setError={setError}
                  fields={{ year: 'move_in_date3', month: 'move_in_date2', day: 'move_in_date1' }}
                  required={false}
                  ageLimit={false}
                />
              </div>
              <div className="mb-6">
                <p className="text-xl text-gray mb-2 font-semibold">家用電話</p>
                <div className="flex items-center border-b border-primary">
                  <input
                    maxLength={8}
                    inputMode="numeric"
                    {...homePhoneField}
                    className="flex-1 px-2 outline-none w-full bg-white text-primary"
                    placeholder="(+852)"
                  />
                </div>
                {errors?.home_tel_no && <span className="text-10 text-red-500">*請輸入正確的電話號碼</span>}
              </div>
            </div>
          )}

          <div className="bg-[#fff9f4] py-6 px-4 rounded-lg">
            <div className="flex mb-2">
              <input
                type="checkbox"
                {...register('checkbox1', {
                  required: true,
                  onChange: (e) => {
                    const checked = e.target.checked;
                    if (checked) {
                      setCheckBoxTime((prev) => ({ ...prev, check_box_1_time: new Date() }));
                    }
                  },
                })}
                id="id-checkbox1"
                className="mr-2 mt-1 w-4 h-4"
              />
              <label htmlFor="id-checkbox1" className="text-scorpion text-xs flex-1">
                本人聲明本人
                <span className="text-primary">並非破產中</span>
                人士，而本人並無意申請破產及據本人所知現時並
                <span className="text-primary">無任何有關本人破產呈請在進行中</span>。
              </label>
            </div>
            <div className="flex">
              <input
                type="checkbox"
                {...register('checkbox2', {
                  required: true,
                  onChange: (e) => {
                    const checked = e.target.checked;
                    if (checked) {
                      setCheckBoxTime((prev) => ({ ...prev, check_box_2_time: new Date() }));
                    }
                  },
                })}
                id="id-checkbox2"
                className="mr-2 mt-1 w-4 h-4"
              />
              <label htmlFor="id-checkbox2" className="text-scorpion text-xs flex-1">
                本人確認為<span className="text-primary">香港永久性居民</span>並年齡為<span className="text-primary">18歲或以上人</span>士，本人已詳細閱讀並同意
                <a href={`${process.env.REACT_APP_HOME_URL}/money-lenders-ordinance/`} className="text-primary underline" target="_blank" rel="noreferrer">
                  放債人條例
                </a>
                、
                <a
                  href={`${process.env.REACT_APP_HOME_URL}/privacy-policy-and-personal-information-collection-statement/`}
                  className="text-primary underline"
                  target="_blank"
                  rel="noreferrer"
                >
                  私隱政策及收集個人資料聲明
                </a>
                、
                <a
                  href={`${process.env.REACT_APP_HOME_URL}/terms-of-use/`}
                  className="text-primary underline"
                  target="_blank"
                  rel="noreferrer"
                >
                  私人貸款使用條款
                </a>
                及
                <a href={`${process.env.REACT_APP_HOME_URL}/website-terms/`} className="text-primary underline" target="_blank" rel="noreferrer">
                  網站使用條款
                </a>
                ，亦同意 Cashing Pro Limited 有權向任何第三方人士索取有關本人的資料，包括向信貸資料機構查閱及提供本人的信貸記錄；亦同意 Cashing Pro Limited 經電話、短訊、WhatsApp、電子郵件通知本人有關貸款的服務信息。
              </label>
            </div>
            {(errors.checkbox1 || errors.checkbox2) && (errors.checkbox1?.type === 'required' || errors.checkbox2?.type === 'required') && (
              <span className="text-10 text-red-500">*必填</span>
            )}
          </div>
          <Common.FormActionRow
            onBack={onClickPrevious}
            nextLabel="下一步"
            disabled={isSubmitting}
            loading={isSubmitting}
          />
          <div className="hidden">
            <button disabled={isSubmitting} type="submit" className={`rounded-full text-white text-xl px-14 py-3 ${isSubmitting ? 'bg-mercury' : 'bg-primary'}`}>
              下一步
            </button>
          </div>
        </div>
      </div>
    </form>
  );
}

export default QuickCashForm;
